import type { ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { UserProvider } from "@/context/userContext";
import { BalanceProvider } from "@/context/BalanceContext";
import { GuestUserProvider } from "@/context/GuestUserContext";
import { SocketProvider } from "@/context/SocketContext";
import { AnnouncementProvider } from "@/context/AnnouncementContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { SuggestionSettingsProvider } from "@/context/SuggestionSettingsContext";
import { PromoNotificationProvider } from "@/context/PromoNotificationContext";
import { PlaybackSettingsProvider } from "@/context/PlaybackSettingsContext";
import { PlayerProvider } from "@/context/PlayerContext";
import { SessionProvider } from "@/context/SessionContext";
import { CurrentPlaylistProvider } from "@/context/CurrentPlaylistContext";
import { PersistenceGate } from "@/components/PersistenceGate";
import StorageAccessPrompt from "@/components/StorageAccessPrompt";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { PlatformSettingsProvider } from "@/context/PlatformSettingsContext";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            gcTime: 5 * 60_000,
            refetchOnWindowFocus: false,
            retry: 1,
        },
    },
});

const IdentityProvider = ({ children }: { children: ReactNode }) => (
    <UserProvider>
        <SocketProvider>
            <AnnouncementProvider>
                <BalanceProvider>
                    <GuestUserProvider>{children}</GuestUserProvider>
                </BalanceProvider>
            </AnnouncementProvider>
        </SocketProvider>
    </UserProvider>
);

const UiPreferencesProvider = ({ children }: { children: ReactNode }) => (
    <TooltipProvider>
        <LanguageProvider>
            <SuggestionSettingsProvider>
                <PromoNotificationProvider>{children}</PromoNotificationProvider>
            </SuggestionSettingsProvider>
        </LanguageProvider>
    </TooltipProvider>
);

const PlaybackProvider = ({ children }: { children: ReactNode }) => (
    <PlaybackSettingsProvider>
        <PlayerProvider>{children}</PlayerProvider>
    </PlaybackSettingsProvider>
);

const LiveSessionProvider = ({ children }: { children: ReactNode }) => (
    <SessionProvider>{children}</SessionProvider>
);

const PlaylistDataProvider = ({ children }: { children: ReactNode }) => (
    <CurrentPlaylistProvider>{children}</CurrentPlaylistProvider>
);

export const AppProviders = ({ children }: { children: ReactNode }) => (
    <QueryClientProvider client={queryClient}>
        <PlatformSettingsProvider>
          <DndProvider backend={HTML5Backend}>
            <IdentityProvider>
                <UiPreferencesProvider>
                    <PlaybackProvider>
                        <LiveSessionProvider>
                            <PlaylistDataProvider>
                                <PersistenceGate>
                                    <Toaster />
                                    <Sonner />
                                    {children}
                                    <StorageAccessPrompt />
                                </PersistenceGate>
                            </PlaylistDataProvider>
                        </LiveSessionProvider>
                    </PlaybackProvider>
                </UiPreferencesProvider>
            </IdentityProvider>
          </DndProvider>
        </PlatformSettingsProvider>
    </QueryClientProvider>
);
